import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

import { useAuthService } from "../../Context/AuthContext";
import { useIsSpeaking } from "@livekit/components-react";
import { displayParticipantAvatar, displayUserAvatar } from "../../Helpers/chatHelper/AvatarHelper";

import { ReactComponent as NotSpeakingParticipantIcon } from "../../Assets/iconsv2/controlButtons/participantNotSeakingIcon.svg"
import { useTranslate } from '@tolgee/react';
import { getOrdinalSuffix } from "../../Helpers/callHelper/mapSpeakOrder";

import AudioWaveformBadge, { MiniWave } from "../GenericComponents/AudioWaveformBadge";
import { RaisedHandIcon, useIsHandRaised } from "./RaisedHands";
import { colors } from "../../Assets/styles/colors";
import { useElementSize } from "../../Helpers/callHelper/useElementSize";
import { Track } from "livekit-client";
import { useVideoQualityFocus } from "./VideoQualityTier";
import {
  AvatarWrapper, Status, Name, VideoContainer,
  NameBadge, NameInfo, NameBadgeText, StatusRow, StatusText, StatusAmp, MutedIconWrapper,
  NameStrip, NameStripText,
} from "./ParticipantSharedStyles";

const ParticipantComponent = ({
  participant,
  callInfo,
  grid,
  focused,
  disableScreenShareDisplay,
  focusParticipant,
  speakersQueue,
  compact
}) => {
  const { ref: tileRef, width: tileWidth, height: tileHeight } = useElementSize();
  useVideoQualityFocus(
    participant,
    disableScreenShareDisplay ? [Track.Source.Camera] : [Track.Source.Camera, Track.Source.ScreenShare],
    focused
  );
  const { t } = useTranslate();
  const isConference = !!callInfo?.conference_id;
  
  let tracks = participant.getTrackPublications();
  const [vTrack, setVTrack] = useState(null);
  const videoRef = useRef(null);
  const [participantInfoFromCall, setParticipantInfoFromCall] = useState(null);
  const { user } = useAuthService();
  const isUserspeaking = useIsSpeaking(participant);
  const [isEmotiiSpeaking, setIsEmotiiSpeaking] = useState(false);
  useEffect(() => {
    const onStart = (e) => {
      if (String(e.detail?.userId) === String(participant.identity)) {
        setIsEmotiiSpeaking(true);
      }
    };
    const onEnd = (e) => {
      if (String(e.detail?.userId) === String(participant.identity)) {
        setIsEmotiiSpeaking(false);
      }
    };
    window.addEventListener("emotii-playback-start", onStart);
    window.addEventListener("emotii-playback-end",   onEnd);
    return () => {
      window.removeEventListener("emotii-playback-start", onStart);
      window.removeEventListener("emotii-playback-end",   onEnd);
    };
  }, [participant.identity]);
  const fontSize = tileWidth ? Math.min(tileWidth / 10, tileHeight / 10, 40) : 16;
  const avatarSize = tileWidth ? Math.min(tileWidth / 3, tileHeight / 3, 80) : 80;
  const badgeFontSize = tileWidth
    ? Math.max(10, Math.min(tileWidth / 28, tileHeight / 10, 14))
    : 11;
  const badgeStatusFontSize = Math.max(9, Math.min(badgeFontSize * 0.75, 12));
  const [mutedMicrophone, setMutedMicrophone] = useState(false)
  const handRaised = useIsHandRaised(participant.identity);
  const [orderToSpeak, setOrderToSpeak] = useState("")


  const calculateQueqeOrder = (speakersQueue) => {
    let userOrderNr = speakersQueue?.findIndex((item) => item.user_id == participant.identity)

    if (userOrderNr === 1) {
      setOrderToSpeak(t('call_modal.next_to_speak'))
    } else
      if (userOrderNr > 1) {
        let order = getOrdinalSuffix(userOrderNr);
        setOrderToSpeak(order + " " + t('call_modal.to_speak'))
      } else {
        setOrderToSpeak("")
      }

  }

  const getParticipantInfoFromCall = () => {
    let info;
    if (isConference && participant?.isLocal && user) {
      info = user;
    } else {
      let foundInOthers = callInfo?.other_participants?.find(
        (p) => p.id == participant.identity
      );

      if (participant.identity == callInfo?.created_by?.id) {
        info = callInfo.created_by;
      } else if (foundInOthers) {
        info = foundInOthers;
      } else if (user && participant.identity == user.id) {
        info = user;
      } else {
        info = {
          name: participant.name,
        };
      }
    }

    if (info) {
      setParticipantInfoFromCall(info);
    }
  };

  useEffect(() => {
    let videoTrack = tracks.find(
      (track) => track.source === "camera" && track.track != null
    );
    let screenShareTrack = tracks.find(
      (track) => track.source === "screen_share" && track.track != null
    );

    if (videoTrack && videoTrack?.track) {
      setVTrack(videoTrack);
      videoTrack.track.attach(videoRef.current);
    } else if (
      screenShareTrack &&
      screenShareTrack.track &&
      !disableScreenShareDisplay
    ) {
      setVTrack(screenShareTrack);
      screenShareTrack.track.attach(videoRef.current);
    } else {
      setVTrack(null);
    }
  }, [tracks]);


  useEffect(() => {
    const allTracks = participant.getTrackPublications();
    const audioTrackPub = allTracks.find(
      (track) => track.source === "microphone" && track.track != null
    );

    const handleMuted = () => setMutedMicrophone(true);
    const handleUnmuted = () => setMutedMicrophone(false);

    const handleTrackSubscribed = (track) => {
      if (track.kind === 'audio') {
        setMutedMicrophone(track.isMuted);
        track.on('muted', handleMuted);
        track.on('unmuted', handleUnmuted);
      }
    };

    let audioTrack;

    if (audioTrackPub && audioTrackPub.track) {
      audioTrack = audioTrackPub.track;
      setMutedMicrophone(audioTrack.isMuted);
      audioTrack.on('muted', handleMuted);
      audioTrack.on('unmuted', handleUnmuted);
    }

    participant.on('trackSubscribed', handleTrackSubscribed);

    return () => {
      participant.off('trackSubscribed', handleTrackSubscribed);

      if (audioTrack) {
        audioTrack.off('muted', handleMuted);
        audioTrack.off('unmuted', handleUnmuted);
      }
    };
  }, [participant]);



  useEffect(() => {
    getParticipantInfoFromCall();
  }, [participant, callInfo]);

  useEffect(() => {
    calculateQueqeOrder(speakersQueue)
  }, [speakersQueue, participant])

  return (
    <ConnectingContainer
      ref={tileRef}
      grid={grid}
      compact={compact}
      speaking={isUserspeaking}
      isEmotiiSpeaking={isEmotiiSpeaking}
      $isBoth={isUserspeaking && isEmotiiSpeaking}
      onClick={() => focusParticipant && focusParticipant(participant)}
    >
      <RaisedHandIcon identity={participant.identity} />
      {vTrack ? (
        <>
          <VideoContainer>
            <video ref={videoRef} autoPlay muted playsInline />
            {!isUserspeaking && !isEmotiiSpeaking && orderToSpeak !== "" &&
              <SpeakingIconWrapper $handRaised={handRaised}>
                {orderToSpeak}
              </SpeakingIconWrapper>
            }
            {mutedMicrophone &&
              <MutedIconWrapper>
                <NotSpeakingParticipantIcon />
              </MutedIconWrapper>
            }
          </VideoContainer>
          {grid ? (
            <NameStrip>
              {(isUserspeaking || isEmotiiSpeaking) && (
                <MiniWave isEmotii={isEmotiiSpeaking} isBoth={isUserspeaking && isEmotiiSpeaking} small />
              )}
              <NameStripText fontSize={badgeFontSize} $speaking={isUserspeaking} $emotii={isEmotiiSpeaking && !isUserspeaking}>
                {participantInfoFromCall?.name}
              </NameStripText>
              {isUserspeaking && (
                <StatusText fontSize={badgeStatusFontSize} $emotii={false}>{t("call_modal.speaking") + "..."}</StatusText>
              )}
              {isUserspeaking && isEmotiiSpeaking && <StatusAmp fontSize={badgeStatusFontSize}>&amp;</StatusAmp>}
              {isEmotiiSpeaking && (
                <StatusText fontSize={badgeStatusFontSize} $emotii={true}>{t("call_modal.interpreting")}</StatusText>
              )}
            </NameStrip>
          ) : (
            <NameBadge $speaking={isUserspeaking} $emotii={isEmotiiSpeaking}>
              {(isUserspeaking || isEmotiiSpeaking) &&
                <MiniWave isEmotii={isEmotiiSpeaking} isBoth={isUserspeaking && isEmotiiSpeaking} />
              }
              <NameInfo>
                <NameBadgeText fontSize={badgeFontSize} $speaking={isUserspeaking} $emotii={isEmotiiSpeaking && !isUserspeaking}>
                  {participantInfoFromCall?.name}
                </NameBadgeText>
                <StatusRow>
                  {isUserspeaking && (
                    <StatusText fontSize={badgeStatusFontSize} $emotii={false}>{t("call_modal.speaking") + "..."}</StatusText>
                  )}
                  {isUserspeaking && isEmotiiSpeaking && <StatusAmp fontSize={badgeStatusFontSize}>&amp;</StatusAmp>}
                  {isEmotiiSpeaking && (
                    <StatusText fontSize={badgeStatusFontSize} $emotii={true}>{t("call_modal.interpreting")}</StatusText>
                  )}
                </StatusRow>
              </NameInfo>
            </NameBadge>
          )}
        </>
      ) : (
        <AvatarWrapper>
          <AudioWaveformBadge
            participant={participantInfoFromCall}
            isSpeaking={isUserspeaking}
            isEmotiiSpeaking={isEmotiiSpeaking}
            size={avatarSize}
          />
          <Name fontSize={fontSize}>{participantInfoFromCall?.name}</Name>
          {isUserspeaking && isEmotiiSpeaking ? (
            <StatusRow>
              <Status fontSize={badgeFontSize} $speaking={true}>{t("call_modal.speaking") + "..."}</Status>
              <StatusAmp fontSize={badgeFontSize}>&amp;</StatusAmp>
              <Status fontSize={badgeFontSize} $emotii={true}>{t("call_modal.interpreting")}</Status>
            </StatusRow>
          ) : (
            <Status fontSize={badgeFontSize} $speaking={isUserspeaking} $emotii={isEmotiiSpeaking}>
              {isEmotiiSpeaking
                ? t("call_modal.interpreting")
                : isUserspeaking
                  ? t("call_modal.speaking") + "..."
                  : t("call_modal.connected")}
            </Status>
          )}
          {orderToSpeak !== "" && !isUserspeaking && (
            <SpeakingIconWrapper $handRaised={handRaised}>{orderToSpeak}</SpeakingIconWrapper>
          )}
          {mutedMicrophone && (
            <MutedIconWrapper><NotSpeakingParticipantIcon /></MutedIconWrapper>
          )}
        </AvatarWrapper>
      )}
    </ConnectingContainer>
  );
};

export default ParticipantComponent;

const ConnectingContainer = styled.div`
  display: flex;
  ${({ grid, compact }) =>
    grid
      ? `flex: 0 0 calc(25% - 10px); max-height: 150px; min-height: 150px;`
      : compact
        ? `flex: 1 0 calc(25% - 10px); min-height: 140px;`
        : `flex: 1 0 calc(25% - 10px); min-height: 300px;`}
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.callComponent.participantComponentBackground};
  overflow: hidden;
  border-radius: 4px;
  position: relative;
  border: 2px solid ${({ speaking, isEmotiiSpeaking }) =>
    isEmotiiSpeaking ? colors.emotiiPurple
    : speaking ? colors.speakingGreen
    : "transparent"};
  ${({ $isBoth, theme }) => $isBoth && css`
    border-color: transparent;
    background:
      linear-gradient(${theme.callComponent.participantComponentBackground}, ${theme.callComponent.participantComponentBackground}) padding-box,
      linear-gradient(135deg, ${colors.speakingGreen} 50%, ${colors.emotiiPurple} 50%) border-box;
  `}
  transition: border-color 0.3s ease, background 0.3s ease;
`;

const SpeakingIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 20px;
  left: ${({ $handRaised }) => ($handRaised ? "44px" : "20px")};
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: ${({ theme }) => theme.callComponent.speakingLabelBg};
  padding: 5px 10px;
  p {
    font-size: 16px;
    font-weight: 500;
    line-height: 18px;
    color: ${({ theme }) => theme.menu.primaryTextColor};
  }
`;
